import React from 'react';
import { Link } from "react-router-dom";
import lang from "../lang";
import {forceLogout, getAccessToken} from "../../utils/authority";
import MenuItems from "../Sidebar/MenuItems";
import {checkPermission} from "../../utils/common";
import './styles.css';

const LANGUAGES = [
    { code: 'vi', name: 'VI', flag: '/images/vn.png' },
    { code: 'en', name: 'EN', flag: '/images/us.png' },
    { code: 'kr', name: 'KR', flag: '/images/kr.png' }
];

class SidebarMobile extends React.Component {
    constructor(props) {
        super(props);
        this.state = {
            items: [],
            filteredItems: [],
            expandedMenus: {},
            searchText: '',
            isAuth: false,
            currentLang: localStorage.getItem('sas_school_language') || 'vi'
        }
    }

    componentDidMount() {
        let token = getAccessToken();
        this.setState({ isAuth: !!token });
        this.initializeMenu();
    }

    componentDidUpdate(prevProps) {
        // Toggle body class khi sidebar mở/đóng
        if (prevProps.show !== this.props.show) {
            if (this.props.show) {
                document.body.classList.add('sidebar-mobile-open');
            } else {
                document.body.classList.remove('sidebar-mobile-open');
            }
        }
    }

    componentWillUnmount() {
        document.body.classList.remove('sidebar-mobile-open');
    }

    /* Nhóm menu chứa trang hiện tại: ưu tiên khớp URL (longest match), fallback
     * quy đổi parentClass cũ qua 'aliases' của nhóm đã gộp — giống Sidebar desktop. */
    resolveParentClass = () => {
        const path = window.location.pathname;
        let best = null, bestLen = -1;
        for (const item of MenuItems.AdminSchool) {
            if (item.type === 'section') continue;
            const urls = [item.url, ...(item.children || []).map(c => c.url)];
            for (const url of urls) {
                if (!url) continue;
                // '/ad-sas-school' là prefix của MỌI url khác trong menu (base path của app)
                // — chỉ khớp tuyệt đối, không dùng startsWith, để tránh nuốt hết các trang khác.
                const matched = url === '/ad-sas-school' ? path === url : path.startsWith(url);
                if (matched && url.length > bestLen) {
                    best = item.activeClass;
                    bestLen = url.length;
                }
            }
        }
        if (best) return best;

        const { parentClass } = this.props;
        if (!parentClass) return null;
        for (const item of MenuItems.AdminSchool) {
            if (item.type === 'section') continue;
            if (item.activeClass === parentClass || (item.aliases || []).includes(parentClass)) {
                return item.activeClass;
            }
        }
        return parentClass;
    }

    initializeMenu = () => {
        let allMenuItems = JSON.parse(JSON.stringify(MenuItems.AdminSchool));

        for (let i = 0; i < allMenuItems.length; i++) {
            let item = allMenuItems[i];
            if (item.type === 'section') continue;

            if (item.permission === '*') {
                // Sentinel: hiện với mọi tài khoản đăng nhập, không gate theo quyền
                item.isShow = true;
            } else {
                let permissionArr = item.permission.split("|");
                let isShow = false;

                for (let i1 = 0; i1 < permissionArr.length; i1++) {
                    isShow = checkPermission(permissionArr[i1]);
                    if (isShow) break;
                }
                item.isShow = isShow;
            }

            let children = item.children;
            if (children) {
                for (let y = 0; y < children.length; y++) {
                    // Dải phân cách không phải mục bấm được nên không khai `permission`;
                    // đọc tiếp là .split() của undefined làm trắng cả trang.
                    if (children[y].type === 'section') continue;
                    let childPermissionArr = children[y].permission.split("|");
                    let childIsShow = false;

                    for (let y1 = 0; y1 < childPermissionArr.length; y1++) {
                        childIsShow = checkPermission(childPermissionArr[y1]);
                        if (childIsShow) break;
                    }
                    children[y].isShow = childIsShow;
                }
            }
        }

        const resolvedParent = this.resolveParentClass();
        const expandedMenus = {};
        if (resolvedParent) {
            expandedMenus[resolvedParent] = true;
        }

        this.setState({
            items: allMenuItems,
            filteredItems: allMenuItems,
            expandedMenus
        });
    }

    handleSearchChange = (e) => {
        const searchText = e.target.value.toLowerCase();
        this.setState({ searchText });

        if (!searchText.trim()) {
            this.setState({ filteredItems: this.state.items });
            return;
        }

        const filteredItems = this.state.items.map(item => {
            if (!item.isShow) return null;

            const titleMatch = item.title.toLowerCase().includes(searchText);

            let matchingChildren = [];
            if (item.children && item.children.length > 0) {
                matchingChildren = item.children.filter(child =>
                    child.type !== 'section' && child.isShow
                    && child.title.toLowerCase().includes(searchText)
                );
            }

            if (titleMatch || matchingChildren.length > 0) {
                return {
                    ...item,
                    children: matchingChildren.length > 0 ? matchingChildren : item.children,
                    forceExpand: matchingChildren.length > 0
                };
            }

            return null;
        }).filter(Boolean);

        this.setState({ filteredItems });
    }

    toggleMenu = (activeClass) => {
        this.setState(prev => ({
            expandedMenus: {
                ...prev.expandedMenus,
                [activeClass]: !prev.expandedMenus[activeClass]
            }
        }));
    }

    isMenuExpanded = (item) => {
        const { expandedMenus, searchText } = this.state;

        // Force expand khi search
        if (item.forceExpand) return true;
        if (searchText && item.children && item.children.length > 0) return true;

        // Kiểm tra xem user đã toggle menu này chưa
        if (expandedMenus.hasOwnProperty(item.activeClass)) {
            return expandedMenus[item.activeClass];
        }

        // Mặc định mở menu chứa trang hiện tại
        if (item.activeClass === this.resolveParentClass()) return true;

        return false;
    }

    getVisibleChildrenCount = (children) => {
        if (!children) return 0;
        return children.filter(child => child.type !== 'section' && child.isShow).length;
    }

    /**
     * Danh sách con để HIỂN THỊ — giữ dải phân cách, bỏ dải nào không còn mục nào đứng sau
     * (quyền cắt sạch nhóm mà tiêu đề nhóm vẫn nằm đó thì trông như menu hỏng).
     */
    getRenderableChildren = (children) => {
        const shown = (children || []).filter(c => c.type === 'section' || c.isShow);
        return shown.filter((c, i) => c.type !== 'section'
            || (shown[i + 1] && shown[i + 1].type !== 'section'));
    }

    getInitials = (name) => {
        if (!name) return 'U';
        const words = name.trim().split(' ');
        if (words.length >= 2) {
            return (words[0][0] + words[words.length - 1][0]).toUpperCase();
        }
        return name.substring(0, 2).toUpperCase();
    }

    onChangeLang = (langCode) => {
        localStorage.setItem('sas_school_language', langCode);
        window.location.reload();
    }

    handleMenuItemClick = (item) => {
        if (!item.children || item.children.length === 0) {
            this.props.toggleSidebarMobile();
        }
    }

    handleSubmenuClick = () => {
        this.props.toggleSidebarMobile();
    }

    render() {
        const { show, toggleSidebarMobile, activeClass } = this.props;
        const { filteredItems, searchText, isAuth, currentLang } = this.state;
        const username = localStorage.getItem("username") || lang.menu?.guest || 'Khách';
        const resolvedParent = this.resolveParentClass();

        // Section đi kèm nhóm phía sau nó: nếu cả cụm bị ẩn theo quyền thì ẩn luôn section
        const shown = filteredItems.filter(item => item.type === 'section' || item.isShow);
        const visibleItems = shown.filter((item, i) =>
            item.type !== 'section' || (shown[i + 1] && shown[i + 1].type !== 'section')
        );

        return (
            <>
                {/* Overlay */}
                <div
                    className={`sidebar-mobile-overlay ${show ? 'show' : ''}`}
                    onClick={toggleSidebarMobile}
                />

                {/* Sidebar */}
                <div className={`sidebar-mobile ${show ? 'show' : ''}`}>
                    {/* Header */}
                    <div className="sidebar-mobile-header">
                        <Link to="/ad-sas-school" className="sidebar-mobile-logo" onClick={toggleSidebarMobile}>
                            <img src="/images/logo_sas.png" alt="Logo" />
                            <span>Times Schools</span>
                        </Link>
                        <button
                            className="sidebar-mobile-close"
                            onClick={toggleSidebarMobile}
                            type="button"
                        >
                            <i className="fas fa-times"></i>
                        </button>
                    </div>

                    {/* User Info */}
                    {isAuth && (
                        <div className="sidebar-mobile-user">
                            <div className="sidebar-mobile-avatar">
                                {this.getInitials(username)}
                            </div>
                            <div className="sidebar-mobile-user-info">
                                <p className="sidebar-mobile-user-name">{username}</p>
                                <p className="sidebar-mobile-user-role">Người dùng</p>
                            </div>
                        </div>
                    )}

                    {/* Search */}
                    {isAuth && (
                        <div className="sidebar-mobile-search">
                            <div className="sidebar-mobile-search-input">
                                <i className="fas fa-search"></i>
                                <input
                                    type="text"
                                    placeholder="Tìm kiếm menu..."
                                    value={searchText}
                                    onChange={this.handleSearchChange}
                                />
                            </div>
                        </div>
                    )}

                    {/* Menu */}
                    <div className="sidebar-mobile-menu-container">
                        {isAuth && (
                            <>
                                {visibleItems.length === 0 ? (
                                    <div className="sidebar-mobile-no-results">
                                        <i className="fas fa-search"></i>
                                        <p>Không tìm thấy menu phù hợp</p>
                                    </div>
                                ) : (
                                    <ul className="sidebar-mobile-menu">
                                        {visibleItems.map((item, index) => {
                                            if (item.type === 'section') {
                                                return (
                                                    <li key={index} className="sidebar-mobile-section-label">
                                                        <span>{item.title}</span>
                                                    </li>
                                                );
                                            }
                                            const hasChildren = item.children && item.children.length > 0;
                                            const visibleChildrenCount = this.getVisibleChildrenCount(item.children);
                                            const isExpanded = this.isMenuExpanded(item);
                                            const isActive = activeClass === item.activeClass || resolvedParent === item.activeClass;

                                            return (
                                                <li key={index} className="sidebar-mobile-menu-item">
                                                    {hasChildren && visibleChildrenCount > 0 ? (
                                                        <>
                                                            <div
                                                                className={`sidebar-mobile-menu-link ${isActive ? 'active' : ''} ${isExpanded ? 'expanded' : ''}`}
                                                                onClick={() => this.toggleMenu(item.activeClass)}
                                                            >
                                                                <div className="sidebar-mobile-menu-icon">
                                                                    <i className={item.icon.replace('font-18 icon-side-bar', '')}></i>
                                                                </div>
                                                                <span className="sidebar-mobile-menu-text">{item.title}</span>
                                                                <span className="sidebar-mobile-menu-badge">{visibleChildrenCount}</span>
                                                                <i className="fas fa-chevron-right sidebar-mobile-menu-arrow"></i>
                                                            </div>
                                                            <ul className={`sidebar-mobile-submenu ${isExpanded ? 'expanded' : ''}`}>
                                                                {this.getRenderableChildren(item.children).map((child, childIndex) => (
                                                                    child.type === 'section' ? (
                                                                        <li key={childIndex} className="sidebar-mobile-submenu-section">
                                                                            <span>{child.title}</span>
                                                                        </li>
                                                                    ) : (
                                                                        <li key={childIndex} className="sidebar-mobile-submenu-item">
                                                                            <Link
                                                                                to={child.url}
                                                                                className={`sidebar-mobile-submenu-link ${activeClass === child.activeClass ? 'active' : ''}`}
                                                                                onClick={this.handleSubmenuClick}
                                                                            >
                                                                                <span>{child.title}</span>
                                                                            </Link>
                                                                        </li>
                                                                    )
                                                                ))}
                                                            </ul>
                                                        </>
                                                    ) : (
                                                        <Link
                                                            to={item.url || '#'}
                                                            className={`sidebar-mobile-menu-link ${isActive ? 'active' : ''}`}
                                                            onClick={() => this.handleMenuItemClick(item)}
                                                        >
                                                            <div className="sidebar-mobile-menu-icon">
                                                                <i className={item.icon.replace('font-18 icon-side-bar', '')}></i>
                                                            </div>
                                                            <span className="sidebar-mobile-menu-text">{item.title}</span>
                                                        </Link>
                                                    )}
                                                </li>
                                            );
                                        })}
                                    </ul>
                                )}
                            </>
                        )}
                    </div>

                    {/* Footer */}
                    <div className="sidebar-mobile-footer">
                        {/* Language Switcher */}
                        <div className="sidebar-mobile-lang">
                            {LANGUAGES.map((language) => (
                                <button
                                    key={language.code}
                                    className={`sidebar-mobile-lang-btn ${currentLang === language.code ? 'active' : ''}`}
                                    onClick={() => this.onChangeLang(language.code)}
                                    type="button"
                                >
                                    <img src={language.flag} alt={language.name} />
                                    <span>{language.name}</span>
                                </button>
                            ))}
                        </div>

                        {/* Logout Button */}
                        {isAuth && (
                            <button
                                className="sidebar-mobile-logout"
                                onClick={forceLogout}
                                type="button"
                            >
                                <i className="fas fa-sign-out-alt"></i>
                                <span>{lang.header?.logout || 'Đăng xuất'}</span>
                            </button>
                        )}
                    </div>
                </div>
            </>
        );
    }
}

export default SidebarMobile;
